import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle, RefreshCw, Search, Unlock, X } from 'lucide-react';
import MainService from '../../services/MainService';

const PAGE_SIZE = 20;

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const parseTotal = (response, fallback = 0) => Number(response?.Total ?? response?.total ?? fallback) || 0;

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB');
};

const formatAmount = (value) => Number(value || 0).toFixed(2);

const getAddressText = (address) => {
  const emirate = address?.Emirate?.Name;
  const location = address?.Location?.Name;
  const area = address?.Location?.Area?.Name || address?.Area?.Name;
  return [emirate, location, area].filter(Boolean).join(', ') || '-';
};

const HoldOrders = () => {
  const [rows, setRows] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [activeKeyword, setActiveKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [unholdTarget, setUnholdTarget] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.OrdersInHoldList({
        Keyword: activeKeyword,
        Skip: (currentPage - 1) * PAGE_SIZE,
        Take: PAGE_SIZE,
      });
      const list = parseList(response);
      setRows(list);
      setTotalCount(parseTotal(response, list.length));
    } catch (err) {
      console.error('Load hold orders error:', err);
      setRows([]);
      setTotalCount(0);
      setError('Failed to load hold orders');
    } finally {
      setLoading(false);
    }
  }, [activeKeyword, currentPage]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const applySearch = () => {
    setActiveKeyword(keyword.trim());
    setCurrentPage(1);
  };

  const refresh = () => {
    setKeyword('');
    setActiveKeyword('');
    setCurrentPage(1);
  };

  const confirmUnhold = async () => {
    if (!unholdTarget?.Id) return;

    try {
      setSaving(true);
      setError('');
      const response = await MainService.ReverseHoldOrders(unholdTarget.Id);
      if (response === 'Success') {
        setSuccess('Success');
        setUnholdTarget(null);
        await loadRows();
      } else {
        setError(String(response || 'Failed to unhold order'));
      }
    } catch (err) {
      console.error('Unhold order error:', err);
      setError('Network Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && <Toast type="error" message={error} onClose={() => setError('')} />}
      {success && <Toast type="success" message={success} onClose={() => setSuccess('')} />}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex flex-col gap-4 border-b border-gray-200 pb-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Hold Orders</h1>
            <p className="mt-1 text-sm text-gray-500">Review orders currently in hold status and release them back to in-store processing.</p>
          </div>
          <button
            type="button"
            onClick={refresh}
            disabled={loading || saving}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            type="text"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') applySearch();
            }}
            placeholder="Search voucher, client, customer"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
          <button
            type="button"
            onClick={applySearch}
            disabled={loading || saving}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
          >
            <Search className="h-4 w-4" />
            Search
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-2 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Hold Order List</h2>
            <p className="text-sm text-gray-500">
              Showing {rows.length} of {totalCount} hold orders
            </p>
          </div>
        </div>

        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[1300px] divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>#</Th>
                <Th>Voucher No</Th>
                <Th>Delivery Date</Th>
                <Th>Client</Th>
                <Th>Client Address</Th>
                <Th>Customer</Th>
                <Th>Customer Address</Th>
                <Th>Product Price</Th>
                <Th>Delivery Charge</Th>
                <Th>Tax</Th>
                <Th>Store Ref No</Th>
                <Th>Status</Th>
                <Th align="right">Options</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <EmptyRow colSpan={13} text="Loading hold orders..." />
              ) : rows.length === 0 ? (
                <EmptyRow colSpan={13} text="No hold orders found." />
              ) : (
                rows.map((row, index) => (
                  <tr key={row.Id} className="hover:bg-gray-50">
                    <Td>{(currentPage - 1) * PAGE_SIZE + index + 1}</Td>
                    <Td strong>{row.VoucherNo}</Td>
                    <Td>{formatDate(row.DeliveryDate)}</Td>
                    <Td>
                      <div>{row.Client?.Name || '-'}</div>
                      <div className="text-xs text-gray-500">{row.Client?.MobileNo1 || ''}</div>
                    </Td>
                    <Td>{getAddressText(row.ClientAddress)}</Td>
                    <Td>
                      <div>{row.Customer?.Name || '-'}</div>
                      <div className="text-xs text-gray-500">{row.Customer?.MobileNo1 || ''}</div>
                    </Td>
                    <Td>{getAddressText(row.CustomerAddress)}</Td>
                    <Td>{formatAmount(row.ProductPrice)}</Td>
                    <Td>{formatAmount(row.DeliveryCharge)}</Td>
                    <Td>{formatAmount(row.TAX)}</Td>
                    <Td>{row.StoreRefNo ?? 0}</Td>
                    <Td>{row.Status?.Name || 'Hold'}</Td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => setUnholdTarget(row)}
                        disabled={saving}
                        className="inline-flex items-center gap-2 rounded-lg bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700 hover:bg-green-100 disabled:opacity-50"
                      >
                        <Unlock className="h-3.5 w-3.5" />
                        Unhold
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between">
          <span>
            Showing {rows.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0} to {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount} orders
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={currentPage <= 1 || loading}
              className="rounded-lg border border-gray-300 px-3 py-1.5 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              disabled={currentPage >= totalPages || loading}
              className="rounded-lg border border-gray-300 px-3 py-1.5 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {unholdTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
            <div className="border-b border-gray-200 p-5">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-green-100 p-2 text-green-600">
                  <Unlock className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Confirm Unhold</h3>
              </div>
            </div>
            <div className="p-5 text-sm text-gray-600">
              Click confirm to unhold voucher <span className="font-semibold text-gray-900">{unholdTarget.VoucherNo}</span>.
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 p-5">
              <button
                type="button"
                onClick={() => setUnholdTarget(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmUnhold}
                disabled={saving}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Th = ({ children, align = 'left' }) => (
  <th className={`whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 ${align === 'right' ? 'text-right' : 'text-left'}`}>
    {children}
  </th>
);

const Td = ({ children, strong = false }) => (
  <td className={`whitespace-nowrap px-4 py-3 text-sm ${strong ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
    {children}
  </td>
);

const EmptyRow = ({ colSpan, text }) => (
  <tr>
    <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-gray-500">
      {text}
    </td>
  </tr>
);

const Toast = ({ type, message, onClose }) => {
  const isError = type === 'error';
  return (
    <div className={`fixed right-4 top-4 z-50 flex min-w-[280px] items-center gap-3 rounded-lg px-4 py-3 text-white shadow-lg ${isError ? 'bg-red-500' : 'bg-green-500'}`}>
      {isError ? <AlertTriangle className="h-5 w-5" /> : <CheckCircle className="h-5 w-5" />}
      <span className="flex-1 text-sm font-medium">{message}</span>
      <button type="button" onClick={onClose} className="rounded p-1 hover:bg-white/20">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default HoldOrders;
